// ═══════════════════════════════════════════════════════════════════════════════
// Guard task creation navigation when total task slots are full (§22)
// ═══════════════════════════════════════════════════════════════════════════════

import { useCallback, useRef } from 'react';
import type { NavigateFunction } from 'react-router-dom';
import type { TotalTaskUsageSnapshot } from '@fdh/shared';
import { settingsApi } from '../lib/api';
import { emitTotalTaskLimitIfReached } from '../lib/limitError';

export const useTotalTaskCreationGate = () => {
  const totalTasksRef = useRef<TotalTaskUsageSnapshot | undefined>(undefined);

  const refreshTotalTasksSnapshot = useCallback(async (): Promise<TotalTaskUsageSnapshot | undefined> => {
    const res = await settingsApi.getUsage();
    const snapshot = res.success && res.data ? res.data.totalTasks : undefined;
    totalTasksRef.current = snapshot;
    return snapshot;
  }, []);

  const guardOpenCreateTaskChoice = useCallback(
    async (openChoice: () => void): Promise<boolean> => {
      const snapshot = await refreshTotalTasksSnapshot();
      if (emitTotalTaskLimitIfReached(snapshot)) {
        return false;
      }
      openChoice();
      return true;
    },
    [refreshTotalTasksSnapshot],
  );

  const guardNavigateToCreateTask = useCallback(
    async (
      navigate: NavigateFunction,
      path: string,
      onBeforeNavigate?: () => void,
    ): Promise<boolean> => {
      const snapshot = totalTasksRef.current ?? (await refreshTotalTasksSnapshot());
      if (emitTotalTaskLimitIfReached(snapshot)) {
        onBeforeNavigate?.();
        return false;
      }
      onBeforeNavigate?.();
      navigate(path);
      return true;
    },
    [refreshTotalTasksSnapshot],
  );

  return {
    guardOpenCreateTaskChoice,
    guardNavigateToCreateTask,
  };
};
